/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'styled-components/macro';
import Button from 'components/atoms/Button';
import Table from 'components/molecules/Table';
import Toast from 'components/molecules/Toast';
import Loaders from 'components/atoms/Loaders';
import ConfigurationForm from 'components/organisms/ConfigurationForm';
import { MqttContext } from 'context/mqttContext';
import { useContextHook } from 'use-context-hook';
import devicesConfigService from 'services/devicesConfigService';
import defaultConfigurations from 'helpers/schema-tms-config.json';
import { AuthContext } from 'context/authContext';
import ModalContainer from 'components/molecules/ModalContainer';

function BulkDeviceConfigUpdate({ onClose }) {
  const [loading, setLoading] = useState(false);
  const selectedDevicesRef = useRef({});
  const [selectedDevicesState, setSelectedDevicesState] = useState({});
  const [configuration, setConfiguration] = useState(defaultConfigurations);
  const syncStatusRef = useRef({});
  const [syncStatusState, setSyncStatusState] = useState({});

  const { user } = useContextHook(AuthContext, { user: 1 });
  const { connRef, resub, subscribe } = useContextHook(MqttContext, { connRef: 1, resub: 1, subscribe: 1 });

  const { active_devices_data, active_devices_loading } = devicesConfigService.GetActiveDevices({
    page: 1,
    pageSize: 1000,
  });

  useEffect(() => {
    if (user?._id) subscribe(user._id);
  }, [resub, subscribe, user?._id]);

  useEffect(() => {
    const handleDeviceSync = ({ detail }) => {
      const newSyncData = {
        ...syncStatusRef.current[detail.serial_number],
        sync_mode: detail.sync_mode,
      };
      if (detail.last_sync) {
        newSyncData.last_sync = new Date(detail.last_sync).toLocaleString();
      }
      syncStatusRef.current = {
        ...syncStatusRef.current,
        [detail.serial_number]: newSyncData,
      };

      setSyncStatusState({ ...syncStatusRef.current });
    };

    window.addEventListener(user._id, handleDeviceSync);
    return () => {
      window.removeEventListener(user._id, handleDeviceSync);
      Object.keys(syncStatusRef.current).forEach(serialNumber => {
        const client = connRef.current;
        if (!client || typeof client.isConnected !== 'function' || !client.isConnected()) return;
        try {
          client.send(serialNumber, JSON.stringify({ type: 'sync_mode_off', callbackQueue: user._id }), 2, false);
        } catch (error) {
          // ignore
        }
      });
      selectedDevicesRef.current = {};
      setSelectedDevicesState({});
    };
  }, []);

  const handleDeviceSelection = (serialNumber, isChecked) => {
    selectedDevicesRef.current = {
      ...selectedDevicesRef.current,
      [serialNumber]: isChecked,
    };
    setSelectedDevicesState({ ...selectedDevicesRef.current });

    const client = connRef.current;
    const canSend = client && typeof client.isConnected === 'function' && client.isConnected();

    if (isChecked) {
      if (canSend) {
        try {
          client.send(serialNumber, JSON.stringify({ type: 'sync_mode_on', callbackQueue: user._id }), 2, false);
        } catch (error) {
          // ignore
        }
      }
    } else if (canSend) {
      try {
        client.send(serialNumber, JSON.stringify({ type: 'sync_mode_off', callbackQueue: user._id }), 2, false);
      } catch (error) {
        // ignore
      }
    }
  };

  const handleSelectAll = () => {
    const allSelected = Object.keys(selectedDevicesRef.current).length === active_devices_data.devices.length;
    const newSelection = {};

    if (!allSelected) {
      active_devices_data.devices.forEach(device => {
        newSelection[device.serial_number] = true;
        const client = connRef.current;
        if (!client || typeof client.isConnected !== 'function' || !client.isConnected()) return;
        try {
          client.send(
            device.serial_number,
            JSON.stringify({ type: 'sync_mode_on', callbackQueue: user._id }),
            2,
            false,
          );
        } catch (error) {
          // ignore
        }
      });
    } else {
      active_devices_data.devices.forEach(device => {
        const client = connRef.current;
        if (!client || typeof client.isConnected !== 'function' || !client.isConnected()) return;
        try {
          client.send(
            device.serial_number,
            JSON.stringify({ type: 'sync_mode_off', callbackQueue: user._id }),
            2,
            false,
          );
        } catch (error) {
          // ignore
        }
      });
    }

    selectedDevicesRef.current = newSelection;
    setSelectedDevicesState({ ...selectedDevicesRef.current });
  };

  const handleUpdateConfig = async () => {
    const selectedSerialNumbers = Object.keys(selectedDevicesRef.current).filter(
      key => selectedDevicesRef.current[key],
    );

    if (selectedSerialNumbers.length === 0) {
      Toast({
        type: 'error',
        message: 'Please select at least one device',
      });
      return;
    }

    if (!connRef.current || typeof connRef.current.isConnected !== 'function' || !connRef.current.isConnected()) {
      Toast({
        type: 'error',
        message: 'MQTT connection not available',
      });
      return;
    }

    try {
      setLoading(true);

      selectedSerialNumbers.forEach(serialNumber => {
        const syncSettings = {
          type: 'sync_settings',
          settings: configuration,
          callbackQueue: user._id,
        };

        try {
          connRef.current.send(serialNumber, JSON.stringify(syncSettings), 2, false);
        } catch (error) {
          // ignore
        }
      });

      setTimeout(async () => {
        try {
          const currentSyncStatus = syncStatusRef.current;
          handleSelectAll();
          const response = await devicesConfigService.updateDevicesConfig({
            serial_numbers: selectedSerialNumbers.map(sn => ({
              sn,
              wasUpdated: currentSyncStatus[sn]?.last_sync && currentSyncStatus[sn]?.last_sync !== '---',
              updatedAt: currentSyncStatus[sn]?.last_sync,
            })),
            configuration,
          });

          let updatedCount = 0;
          selectedSerialNumbers.forEach(sn => {
            if (currentSyncStatus[sn]?.last_sync && currentSyncStatus[sn]?.last_sync !== '---') {
              updatedCount += 1;
            }
          });
          Toast({
            type: 'success',
            message: `Configuration updated for ${updatedCount} device(s)`,
          });
        } catch (error) {
          Toast({
            type: 'error',
            message: error.message ?? 'Something went wrong',
          });
        } finally {
          setLoading(false);
        }
      }, 5000);
    } catch (error) {
      Toast({
        type: 'error',
        message: error.message ?? 'Something went wrong',
      });
      setLoading(false);
    }
  };
  const { rowsData } = useMemo(
    () => ({
      rowsData: active_devices_data.devices.map(device => [
        <input
          key={device._id}
          type="checkbox"
          checked={selectedDevicesRef.current[device.serial_number] || false}
          onChange={e => handleDeviceSelection(device.serial_number, e.target.checked)}
        />,
        device.title || '---',
        device.serial_number,
        syncStatusRef.current[device.serial_number]?.sync_mode ? (
          <span css="color: #3aca27;">ON</span>
        ) : (
          <span css="color: #d34242;">OFF</span>
        ),
        syncStatusRef.current[device.serial_number]?.last_sync,
      ]),
    }),
    [active_devices_data.devices, selectedDevicesState, syncStatusState],
  );

  const columnNames = ['Select', 'Title', 'Serial Number', 'Sync Mode', 'Last Sync'];
  const getMergedConfig = config => {
    const updatedSettings = { ...defaultConfigurations };
    const updateNestedObject = (target, source) => {
      Object.keys(source).forEach(key => {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          if (!target[key] || typeof target[key] !== 'object') {
            target[key] = {};
          }
          updateNestedObject(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      });
    };
    updateNestedObject(updatedSettings, config);
    return updatedSettings;
  };
  return (
    <Loaders loading={loading}>
      <div
        css={`
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}>
        <div
          css={`
            display: flex;
            gap: 15px;
            align-items: center;
            margin-bottom: 20px;
            float: left;
          `}>
          <Button type="outline" onClick={handleSelectAll}>
            {Object.keys(selectedDevicesRef.current).length === active_devices_data.devices.length
              ? 'Deselect All'
              : 'Select All'}
          </Button>
        </div>
        <ModalContainer
          btnComponent={({ onClick }) => (
            <div
              css={`
                gap: 15px;
                float: right;
              `}>
              <Button
                type="primary"
                onClick={onClick}
                disabled={
                  Object.keys(selectedDevicesRef.current).filter(key => selectedDevicesRef.current[key]).length === 0
                }>
                Configure Selected Devices
              </Button>
            </div>
          )}
          title="Configure Devices"
          width={1200}
          content={({ onClose }) => (
            <ConfigurationForm
              configuration={getMergedConfig(configuration)}
              onClose={onClose}
              getConfig={setConfiguration}
              getOnlyTouchedKeys
            />
          )}
        />
        <div
          css={`
            gap: 15px;
            margin-bottom: 20px;
            float: right;
          `}>
          <span css="color: gray;">
            {Object.keys(selectedDevicesRef.current).filter(key => selectedDevicesRef.current[key]).length} devices
            selected
          </span>
        </div>
      </div>

      <Table width={1200} loading={active_devices_loading} rowsData={rowsData} columnNames={columnNames} noPadding />

      <div css="display: flex; justify-content: flex-end; gap: 15px; margin-top: 20px;">
        <Button type="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleUpdateConfig}
          disabled={
            Object.keys(selectedDevicesRef.current).filter(key => selectedDevicesRef.current[key]).length === 0
          }>
          Update Configuration
        </Button>
      </div>
    </Loaders>
  );
}

export default BulkDeviceConfigUpdate;
